import { tt } from '@agos/shared'

import { gatewayMessage } from './payment-archive'

/**
 * What *Platby online - archív* may say about the gateway and the two checkout rules to somebody who may not read the
 * gateway's settings - FRONTEND.md §9.750, FB-1065.
 *
 * **The defect.** The page read `GET /settings/payments` for whoever opened it, and that read is
 * `@RequireAuth("main_admin")`. So a Nesvady administrator or a Šurany waiter holding `online_payments_view` was told
 * *Stav platobnej brány sa nepodarilo načítať*, and the two *Pravidlá pokladne* switches were drawn **off** from
 * `prefsOf({})` - a false claim about how the checkout behaves, on the screen somebody opens to find out.
 *
 * **The fix reads what they may read.** `GET /settings/payments/public` is unauthenticated - the checkout reads it - and
 * answers three booleans, two of which **are** the two rules: `defaultOnline` and `allowOnlinePickup` come off the same
 * stored row as the staff answer's, so they fill the switches honestly. The third, `isActive`, is
 * `PaymentSettings.usable()` - switched on **and** both credentials stored - which is narrower than the staff answer can
 * say: it cannot tell a live gateway from a test one, nor *switched off* from *not configured*. So the line says what it
 * does know, which is whether the checkout offers card online, and names both possibilities when it does not.
 */

/** The one role `GET /settings/payments` admits - `OnlinePaymentController.settings`, `@RequireAuth("main_admin")`. */
export const SETTINGS_READER_ROLE = 'main_admin'

/** Whether this viewer may read the gateway's own settings, rather than the checkout's public three flags. */
export function readsPaymentSettings(systemRole: string | undefined): boolean {
  return systemRole === SETTINGS_READER_ROLE
}

/** The public answer, as facts this screen can state. */
export interface PublicPaymentFacts {
  /** `PaymentSettings.usable()`: switched on and both credentials stored - the answer's `isActive`, not the stored flag. */
  usable: boolean
  defaultOnline: boolean
  allowOnlinePickup: boolean
}

/**
 * Read `GET /settings/payments/public`. **Null unless all three are booleans** - the server answers `null` for a flag
 * it could not read (`bool()` in the controller), and a missing flag drawn as *off* is the false claim this exists to
 * end.
 */
export function publicPaymentFactsOf(payload: unknown): PublicPaymentFacts | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null
  const row = payload as Record<string, unknown>
  if (
    typeof row.isActive !== 'boolean' ||
    typeof row.defaultOnline !== 'boolean' ||
    typeof row.allowOnlinePickup !== 'boolean'
  ) {
    return null
  }
  return { usable: row.isActive, defaultOnline: row.defaultOnline, allowOnlinePickup: row.allowOnlinePickup }
}

/** What the gateway line can say from the public answer - three states, because that answer carries no more. */
export type PublicGatewayState = 'usable' | 'unusable' | 'unknown'

export function publicGatewayState(facts: PublicPaymentFacts | null): PublicGatewayState {
  if (facts === null) return 'unknown'
  return facts.usable ? 'usable' : 'unusable'
}

/**
 * The gateway line for a viewer who reads the public answer. **A `switch` with no `default`**, so a fourth state is
 * TS2366 rather than a line borrowing another's words. *Unknown* is `gatewayMessage`'s own sentence, so the two reads
 * say a failure one way.
 */
export function publicGatewayMessage(state: PublicGatewayState): string {
  switch (state) {
    case 'usable':
      return tt('Platba kartou online sa pri objednávke ponúka - brána je zapnutá a nastavená.')
    case 'unusable':
      return tt('Platba kartou online sa pri objednávke neponúka - brána je vypnutá alebo nie je nastavená.')
    case 'unknown':
      return gatewayMessage('unknown', '')
  }
}
